import { useState, useEffect } from 'react';
import { uploadExposureFile, viewExposureFile } from './exposureUpload';

type Notify = (
  severity: 'success' | 'info' | 'warn' | 'error',
  summary: string,
  detail: string
) => void;

interface Deps {
  schoolId: number;
  // Owner id for the uploaded file name (student id when editing, else school).
  getOwnerId: () => string;
  postExposureUploads: (arg: {
    records: {
      PhotoUpload: string;
      VideoUpload: string;
      school_id: number | string;
    };
  }) => { unwrap: () => Promise<any> };
  // Already-uploaded photo/video from the GET response (for patching the UI).
  savedUploads?: { photo: string; video: string };
  notify: Notify;
  // Full-page loader controls (from useUI).
  showLoader: () => void;
  hideLoader: () => void;
}

const MAX_SIZE = 15 * 1024 * 1024; // 15 MB

// Extensions the DrRadhaFileupld backend accepts per folder.
const ALLOWED_EXT: Record<'image' | 'video', string[]> = {
  image: ['png', 'jpg', 'jpeg'],
  video: ['mp4'],
};

function getExt(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

// Photo/video upload state + handlers for the Student Exposure Visit page.
export function useExposureUploads({
  schoolId,
  getOwnerId,
  postExposureUploads,
  savedUploads,
  notify,
  showLoader,
  hideLoader,
}: Deps) {
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [uploadSubmitted, setUploadSubmitted] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [imageKey, setImageKey] = useState('');
  const [videoKey, setVideoKey] = useState('');
  // Display names for the uploaded files (shown in the UI).
  const [imageName, setImageName] = useState('');
  const [videoName, setVideoName] = useState('');
  // True when the photo/video were already uploaded (patched from GET).
  const [isPatched, setIsPatched] = useState(false);

  // Derive a readable file name from a stored key/url.
  const nameFromKey = (val: string) => {
    if (!val) return '';
    const noQuery = val.split('?')[0];
    const seg = noQuery.split('/').pop() || noQuery;
    try {
      return decodeURIComponent(seg);
    } catch {
      return seg;
    }
  };

  // Patch already-uploaded photo/video from the GET response so the upload
  // section shows them (View buttons + file name) — keep the section visible.
  useEffect(() => {
    const photo = savedUploads?.photo ?? '';
    const video = savedUploads?.video ?? '';
    if (photo) {
      setImageKey(photo);
      setImageName(nameFromKey(photo));
    }
    if (video) {
      setVideoKey(video);
      setVideoName(nameFromKey(video) || 'Uploaded video');
    }
    if (photo || video) setIsPatched(true);
  }, [savedUploads?.photo, savedUploads?.video]);
  // Bump only on clear, so the native <input type=file> keeps showing the
  // selected filename after a pick (remounting only when cleared).
  const [photoResetKey, setPhotoResetKey] = useState(0);
  const [videoResetKey, setVideoResetKey] = useState(0);

  // Upload a file as soon as it is selected (image/video only, max 15 MB).
  async function onFileSelect(kind: 'image' | 'video', file: File | null) {
    // Reset the stored key for a fresh pick (don't remount the input, so the
    // native filename stays visible).
    if (kind == 'image') setImageKey('');
    else setVideoKey('');
    if (!file) return;

    // Validate the extension against what the backend accepts for this folder.
    const ext = getExt(file.name);
    if (!ALLOWED_EXT[kind].includes(ext)) {
      if (kind == 'image') setPhotoFile(null);
      else setVideoFile(null);
      const allowed = ALLOWED_EXT[kind].join(', ').toUpperCase();
      return notify(
        'warn',
        'Invalid file',
        `Only ${allowed} ${kind} files are allowed.`
      );
    }
    if (file.size > MAX_SIZE) {
      if (kind == 'image') setPhotoFile(null);
      else setVideoFile(null);
      return notify('warn', 'File too large', 'Maximum allowed size is 15 MB.');
    }

    if (kind == 'image') setPhotoFile(file);
    else setVideoFile(file);

    const setBusy = kind == 'image' ? setIsImageUploading : setIsVideoUploading;
    setBusy(true);
    showLoader();
    try {
      const { key } = await uploadExposureFile(kind, getOwnerId(), file);
      if (kind == 'image') {
        setImageKey(key);
        setImageName(file.name);
      } else {
        setVideoKey(key);
        setVideoName(file.name);
      }
      setIsPatched(false); // user replaced with a fresh upload
      notify(
        'success',
        'Uploaded',
        `${kind == 'image' ? 'Photo' : 'Video'} uploaded successfully.`
      );
    } catch (err: any) {
      console.error('Exposure upload failed:', err?.response ?? err);
      const detail =
        err?.response?.data?.message ||
        err?.message ||
        'Upload failed. Please try again.';
      notify('error', 'Error', detail);
    } finally {
      setBusy(false);
      hideLoader();
    }
  }

  function onClearFile(kind: 'image' | 'video') {
    if (kind == 'image') {
      setPhotoFile(null);
      setImageKey('');
      setImageName('');
      setPhotoResetKey((k) => k + 1); // remount input → clears native filename
    } else {
      setVideoFile(null);
      setVideoKey('');
      setVideoName('');
      setVideoResetKey((k) => k + 1);
    }
    // Clearing unlocks the section so the user can re-pick and re-submit.
    setIsPatched(false);
    setIsSubmitted(false);
  }

  async function onViewFile(kind: 'image' | 'video') {
    const key = kind == 'image' ? imageKey : videoKey;
    if (!key) return notify('warn', 'Warning', 'No file uploaded yet.');
    try {
      const res = await viewExposureFile(key);
      const url = res?.url || res?.signedUrl || res?.data || res;
      if (typeof url == 'string') window.open(url, '_blank');
      else notify('error', 'Error', 'Could not open the file.');
    } catch {
      notify('error', 'Error', 'Could not open the file.');
    }
  }

  // Submit button → POST the stored photo/video keys.
  async function onUploadSubmit() {
    setUploadSubmitted(true);
    if (!imageKey || !videoKey) {
      return notify('warn', 'Warning', 'Please upload both photo and video.');
    }
    showLoader();
    try {
      const res = await postExposureUploads({
        records: {
          PhotoUpload: imageKey,
          VideoUpload: videoKey,
          school_id: schoolId,
        },
      }).unwrap();
      if (res?.dataStatus) {
        notify('success', 'Success', res.message || 'Submitted successfully.');
        setIsSubmitted(true);
      } else notify('error', 'Error', res?.message || 'Submit failed.');
    } catch {
      notify('error', 'Error', 'Something went wrong. Please try again.');
    } finally {
      hideLoader();
    }
  }

  return {
    photoFile,
    videoFile,
    uploadSubmitted,
    isSubmitted,
    isPatched,
    // Lock the upload UI (disable fields + hide Submit) once the files are
    // already saved — either submitted this session or patched from GET.
    isUploadLocked: isSubmitted || isPatched,
    isImageUploading,
    isVideoUploading,
    imageKey,
    videoKey,
    imageName,
    videoName,
    photoResetKey,
    videoResetKey,
    onFileSelect,
    onClearFile,
    onViewFile,
    onUploadSubmit,
  };
}
